import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Icon, Skeleton } from '@tb/ui';
import { api } from '../api/endpoints';
import { Screen } from '../components/Layout';
import { CopyableUrl } from '../components/CopyableUrl';
import { StatusBadge } from '../components/StatusBadge';
import { useT } from '../i18n/LocaleProvider';
import { formatBytes, formatDate, shortenBagId } from '../lib/format';

export function SiteScreen() {
  const { siteId = '' } = useParams();
  const navigate = useNavigate();
  const t = useT();

  const siteQuery = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => api.sites.get(siteId),
    refetchInterval: (query) => {
      const status = query.state.data?.draftVersion?.status;
      return status === 'BUILDING' || status === 'PUBLISHING' ? 4000 : false;
    },
  });

  if (siteQuery.isPending) {
    return (
      <Screen back="/">
        <div className="tb-screen">
          <Skeleton height={120} />
          <Skeleton height={90} />
        </div>
      </Screen>
    );
  }

  if (siteQuery.isError) {
    return (
      <Screen back="/">
        <div className="tb-screen">
          <Alert tone="danger" title={t('site.loadFailed')}>
            {(siteQuery.error as Error).message}
          </Alert>
        </div>
      </Screen>
    );
  }

  const site = siteQuery.data;
  const live = site.liveVersion;
  const draft = site.draftVersion;

  return (
    <Screen title={site.domain} back="/">
      <div className="tb-screen">
        <Card>
          <div className="domain-row">
            <Icon name="domain" size={22} />
            <div className="tb-grow tb-stack" style={{ gap: 2, minWidth: 0 }}>
              <span className="domain-row__name tb-truncate">{site.domain}</span>
              <span className="tb-hint">
                {site.kind === 'TEMPLATE'
                  ? t('site.kind.template', { name: site.templateCode ?? '' })
                  : t('site.kind.custom')}
              </span>
            </div>
            {live ? (
              <Badge tone="live" icon="success">
                {t('site.live')}
              </Badge>
            ) : (
              <Badge>{t('site.notPublished')}</Badge>
            )}
          </div>

          {live ? (
            <>
              <div className="kv">
                <span className="kv__key">{t('site.field.bag')}</span>
                <span className="kv__value tb-mono">{shortenBagId(live.bagId)}</span>
              </div>
              <div className="kv">
                <span className="kv__key">{t('site.field.published')}</span>
                <span className="kv__value">{formatDate(live.publishedAt)}</span>
              </div>
              <div className="kv">
                <span className="kv__key">{t('site.field.size')}</span>
                <span className="kv__value">{formatBytes(live.sizeBytes)}</span>
              </div>
            </>
          ) : (
            <span className="tb-hint">{t('site.nothingOnChain')}</span>
          )}
        </Card>

        {live ? (
          <Card>
            <span className="tb-label">{t('site.openIt')}</span>

            <div className="tb-stack" style={{ gap: 6 }}>
              <span className="tb-hint">{t('site.tonAddress')}</span>
              <CopyableUrl icon="domain" url={site.domain} label={site.domain} />
            </div>

            <span className="tb-hint">{t('site.openHint', { domain: site.domain })}</span>
          </Card>
        ) : null}

        {draft && draft.status === 'FAILED' ? (
          <Alert tone="danger" title={t('site.lastFailed')}>
            {draft.error ?? ''}
          </Alert>
        ) : null}

        {draft && (draft.status === 'BUILDING' || draft.status === 'PUBLISHING') ? (
          <Alert tone="info" title={t('site.inProgress')}>
            <div className="tb-row">
              <StatusBadge status={draft.status} />
              <span>
                {draft.status === 'BUILDING' ? t('site.packing') : t('site.waitingChain')}
              </span>
            </div>
          </Alert>
        ) : null}

        {site.hasUnpublishedChanges && draft?.status !== 'PUBLISHING' ? (
          <Alert tone="warning" title={t('site.unpublished.title')}>
            {t('site.unpublished.body')}
          </Alert>
        ) : null}

        {site.needsSiteRecord && draft?.status !== 'PUBLISHING' ? (
          <Alert tone="warning" title={t('site.needsSiteRecord.title')}>
            {t('site.needsSiteRecord.body')}
          </Alert>
        ) : null}

        {site.suspendedAt ? (
          <Alert tone="danger" icon="lock" title={t('site.suspended.title')}>
            <div className="tb-stack" style={{ gap: 10 }}>
              <span>{t('site.suspended.body')}</span>
              <Button icon="sparkles" size="sm" onClick={() => void navigate(`/plans/${siteId}`)}>
                {t('site.renew')}
              </Button>
            </div>
          </Alert>
        ) : !site.isCovered ? (
          <Alert tone="warning" icon="lock" title={t('site.needsPlan.title')}>
            <div className="tb-stack" style={{ gap: 10 }}>
              <span>{t('site.needsPlan.body')}</span>
              <Button icon="sparkles" size="sm" onClick={() => void navigate(`/plans/${siteId}`)}>
                {t('billing.choosePlan')}
              </Button>
            </div>
          </Alert>
        ) : null}

        <div className="tb-stack">
          <Button icon="edit" block onClick={() => void navigate(`/sites/${siteId}/edit`)}>
            {t('site.edit')}
          </Button>

          <Button
            variant="secondary"
            icon="layers"
            block
            onClick={() => void navigate(`/sites/${siteId}/template`)}
          >
            {t('site.changeTemplate')}
          </Button>

          {site.isCovered ? (
            <Button
              variant={site.hasUnpublishedChanges || site.needsSiteRecord ? 'primary' : 'secondary'}
              icon="publish"
              block
              disabled={
                (!site.hasUnpublishedChanges && !site.needsSiteRecord) ||
                draft?.status === 'PUBLISHING'
              }
              onClick={() => void navigate(`/sites/${siteId}/publish`)}
            >
              {site.hasUnpublishedChanges
                ? live
                  ? t('site.publishUpdate')
                  : t('site.publish')
                : site.needsSiteRecord
                  ? t('site.publishFix')
                  : t('site.upToDate')}
            </Button>
          ) : (
            <Button
              variant="primary"
              icon="lock"
              block
              onClick={() => void navigate(`/plans/${siteId}`)}
            >
              {t('billing.choosePlan')}
            </Button>
          )}

          <Button
            variant="ghost"
            icon="history"
            block
            onClick={() => void navigate(`/sites/${siteId}/versions`)}
          >
            {t('site.history')}
          </Button>
        </div>
      </div>
    </Screen>
  );
}
